import React, { useState, useEffect, useRef, useCallback } from 'react';
import debounce from 'lodash/debounce';

const EditableThumbWithLabel = React.forwardRef(
  (
    { value, min, max, step, onChange, formatValue, hideLabel = false },
    ref
  ) => {
    const [inputValue, setInputValue] = useState(String(value));
    const [lastValidValue, setLastValidValue] = useState(value);
    const [isEditing, setIsEditing] = useState(false);

    /**
     * Force re-sync if parent changes value externally
     */
    useEffect(() => {
      if (value !== lastValidValue) {
        setInputValue(String(value));
        setLastValidValue(value);
        setIsEditing(false);
      }
    }, [value, lastValidValue]);

    /**
     * parseAndRevert: 
     * - If user typed "xxxk", multiply by 1000.
     * - If numeric < 100, treat as thousands => 30 => 30000
     * - If final is out of [min..max], revert to last valid.
     * - If parse fails, revert to last valid.
     */
    const parseAndRevert = useCallback(
      (rawStr) => {
        let str = rawStr.trim().toLowerCase();
        const hasK = /k$/.test(str);
        if (hasK) {
          str = str.replace(/k$/, ''); // remove trailing "k"
        }

        let parsed = parseFloat(str.replace(/[^\d.-]/g, ''));
        if (isNaN(parsed)) {
          // revert if not parseable
          return lastValidValue;
        }

        if (hasK) {
          parsed *= 1000;
        } else if (parsed < 1000) {
          parsed *= 1000;
        }

        // Optional step rounding (commented out):
        /*
        if (step && step > 0) {
          parsed = Math.round(parsed / step) * step;
        }
        */

        // If parsed is out of range, revert entirely
        if (parsed > max || parsed < min) {
          return lastValidValue;
        }
        return parsed;
      },
      [min, max, step, lastValidValue]
    );

    // Format large numbers as "XXk"
    const formatCompact = useCallback((num) => {
      if (num >= 1000) {
        return Math.round(num / 1000) + 'k';
      }
      return String(num);
    }, []);

    // Debounced update
    // (reduce from 2000ms to e.g. 500ms if you prefer quicker feedback)
    const debouncedUpdate = useRef(
      debounce((rawStr) => {
        const nextVal = parseAndRevert(rawStr);
        if (nextVal !== lastValidValue) {
          setLastValidValue(nextVal);
          onChange?.(nextVal);
        }
        setInputValue(String(nextVal));
      }, 2000)
    ).current;

    const handleChange = (e) => {
      const val = e.target.value;
      setInputValue(val);
      debouncedUpdate(val);
    };

    const handleFocus = (e) => {
      setIsEditing(true);
      e.target.select();
    };

    const handleKeyDown = (e) => {
      if (e.key === 'Enter') {
        handleBlur();
        e.target.blur();
      } else if (e.key === 'Escape') {
        // revert to last valid
        setInputValue(String(lastValidValue));
        setIsEditing(false);
        e.target.blur();
      }
    };

    const handleBlur = () => {
      debouncedUpdate.flush();
      const nextVal = parseAndRevert(inputValue);
      if (nextVal !== lastValidValue) {
        setLastValidValue(nextVal);
        onChange?.(nextVal);
      }
      // Convert final to "xxk" if needed
      setInputValue(formatCompact(nextVal));
      setIsEditing(false);
    };

    // +/- increments by 1000
    const incrementValue = (delta) => {
      const newVal = lastValidValue + delta;
      // If out of [min..max], we revert to last valid
      if (newVal > max || newVal < min) {
        // do nothing or revert?
        // Here we'll revert (so no change):
        setInputValue(String(lastValidValue));
        return;
      }
      setLastValidValue(newVal);
      setInputValue(formatCompact(newVal));
      onChange?.(newVal);
    };

    return (
      <div
        ref={ref}
        className="relative flex flex-col items-center pointer-events-auto"
        style={{ minHeight: '15px' }}
      >
        {/* Draggable thumb */}
        <div className="w-4 h-4 bg-white border border-zinc-800 shadow-md rounded-full cursor-grab" />

        {/* Label bubble */}
        {!hideLabel && (
          <div
            className="absolute top-full left-1/2 -translate-x-1/2 mt-2 pointer-events-auto group"
            onPointerDown={(e) => e.stopPropagation()}
          >
            <div
              className="
                relative flex items-center justify-center
                bg-white px-2 py-1
                border border-slate-700
                rounded-full shadow text-center
                w-max z-50
                transition-colors
                group-hover:bg-gray-50 group-hover:border-emerald-500
                focus-within:border-emerald-500
              "
              title="Type a value or 'k' for thousands. Press Enter to confirm, Esc to revert."
            >
              {/* Decrement */}
              <button
                type="button"
                onClick={() => incrementValue(-1000)}
                className="text-gray-600 text-xs font-bold px-1 cursor-pointer hover:text-black hover:bg-gray-100 active:scale-95 rounded"
              >
                –
              </button>

              {/* Input */}
              <div className="relative flex items-center mx-1">
                <input
                  type="text"
                  placeholder="30k"
                  value={
                    isEditing
                      ? inputValue
                      : formatValue
                      ? formatValue(value)
                      : formatCompact(value)
                  }
                  onChange={handleChange}
                  onFocus={handleFocus}
                  onKeyDown={handleKeyDown}
                  onBlur={handleBlur}
                  className="bg-white text-gray-800 text-center border-none outline-none w-16 rounded-md cursor-text"
                  style={{ padding: '2px 4px' }}
                />
              </div>

              {/* Increment */}
              <button
                type="button"
                onClick={() => incrementValue(1000)}
                className="text-gray-600 text-xs font-bold px-1 cursor-pointer hover:text-black hover:bg-gray-100 active:scale-95 rounded"
              >
                +
              </button>

              {/* Arrow */}
              <div
                className="
                  absolute top-[-4px] left-1/2 -translate-x-1/2
                  border-l-[4px] border-r-[4px] border-b-[4px]
                  border-l-transparent border-r-transparent border-b-zinc-800
                "
              />
            </div>
          </div>
        )}
      </div>
    );
  }
);

export default EditableThumbWithLabel;